import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Stack,
  Divider,
  alpha,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  ContentCopy,
  OpenInNew,
  AccountBalanceWallet,
  CreditCard,
  QrCode2,
  Send as SendIcon,
} from '@mui/icons-material';
import { GradientCard, GlassCard, AnimatedButton } from '../../components/v2';
import { gradients } from '../../theme/v2Theme';
import QRCode from 'react-qr-code';
import { useParams } from 'react-router-dom';
import {
  isHandleAvailable,
  isSubHandleAvailable,
  getUserAddresses,
} from '../../utils/handleManager';
import Coins from '../../assets/coins/coins.json';
import { ethers } from 'ethers';
import ReactGA from 'react-ga4';
import toast from 'react-hot-toast';
import * as firebase from 'firebase/app';
import 'firebase/database';
import { getDisplayAddress, handleToEnsStyle, hasEthAddress } from '../../utils/ensFormatter';

const coinGradients: { [key: string]: string } = {
  BTC: gradients.sunset,
  ETH: gradients.ocean,
  USDC: gradients.success,
  USDT: gradients.success,
  MATIC: gradients.lavender,
  SOL: gradients.primary,
  BNB: gradients.sunset,
};

export const SendPage: React.FC = () => {
  const { handle, coin, address: urlAddress } = useParams<{ handle?: string; coin: string; address?: string }>();
  const [amount, setAmount] = useState('');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [handleExists, setHandleExists] = useState(false);
  const [address, setAddress] = useState('');
  const [userAddresses, setUserAddresses] = useState<{ [key: string]: string }>({});
  const [web3Loading, setWeb3Loading] = useState(false);
  const [web3Error, setWeb3Error] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  // Check if this is a direct address payment (from /pay/:coin/:address)
  const isDirectAddress = !!urlAddress;

  const coins = Coins as { [key: string]: any };
  const coinKey = coin?.toUpperCase() || 'BTC';
  const coinInfo = coins[coinKey] || {};

  const coinData = {
    name: coinInfo.name || coinKey,
    symbol: coinKey,
    address: address,
    gradient: coinGradients[coinKey] || gradients.primary,
    icon: coinInfo.symbol || coinKey.charAt(0),
  };

  useEffect(() => {
    async function loadAddressData() {
      // If this is a direct address payment (from /pay/:coin/:address)
      if (isDirectAddress && urlAddress) {
        setAddress(urlAddress);
        setHandleExists(true); // Treat as valid
        setLoading(false);

        // Track page view with Google Analytics
        ReactGA.event('open_addrs_direct', {
          coin: coinKey,
          address: urlAddress,
        });
        return;
      }

      // Otherwise, handle-based payment
      if (!handle) {
        setLoading(false);
        return;
      }

      try {
        // Check if handle exists
        const isAvailable = await isHandleAvailable(handle);
        const isSubAvailable = await isSubHandleAvailable(handle);

        let exists = false;
        if (isAvailable) {
          if (!isSubAvailable) {
            exists = true;
          }
        } else {
          exists = true;
        }

        setHandleExists(exists);

        // Load addresses if handle exists
        if (exists) {
          const fetchedAddresses = await getUserAddresses(handle);
          setUserAddresses(fetchedAddresses);
          const coinAddress = fetchedAddresses[coinKey];
          if (coinAddress) {
            setAddress(coinAddress);

            // Track page view with Google Analytics
            ReactGA.event('open_addrs', {
              addrs: handle,
              coin: coinKey,
              address: coinAddress,
            });

            // Track payment request view - increment payment request count
            try {
              const handleRef = firebase.database().ref(`/handles/${handle}`);
              const handleSnap = await handleRef.once('value');
              if (handleSnap.exists()) {
                const uid = handleSnap.val();
                if (uid) {
                  // Increment payment requests
                  firebase.database().ref(`/stats/${uid}/paymentRequests`).transaction((current) => {
                    return (current || 0) + 1;
                  });
                }
              }
            } catch (trackError) {
              console.error('Error tracking payment request:', trackError);
            }
          }
        }
      } catch (error) {
        console.error('Error loading address:', error);
      } finally {
        setLoading(false);
      }
    }

    loadAddressData();
  }, [handle, coinKey, isDirectAddress, urlAddress]);

  // Check URL for amount parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlAmount = urlParams.get('amount');
    if (urlAmount) {
      setAmount(urlAmount);
    }
  }, []);

  // Format payment URI based on coin type (from V1)
  const getPaymentUri = () => {
    if (!coinInfo.uri || !address) return '';

    let formattedUri = coinInfo.uri.replace('{address}', address);

    if (amount && parseFloat(amount) > 0) {
      const amt = amount;

      // Ethereum - convert to wei (18 decimals)
      if (coinKey === 'ETH') {
        let weiAmount = amt;
        if (amt.includes('.')) {
          weiAmount = amt.replace('.', '');
          const decimalPlaces = amt.split('.')[1]?.length || 0;
          weiAmount = weiAmount + '000000000000000000';
          weiAmount = weiAmount.substring(0, weiAmount.length - decimalPlaces + 1);
        } else {
          weiAmount = amt + '000000000000000000';
        }
        formattedUri = formattedUri + '?value=' + weiAmount;
      }
      // ERC20 tokens
      else if (coinInfo.ERC20 === true) {
        let tokenAmount = amt;
        // USDC uses 6 decimals
        if (coinKey === 'USDC' || coinKey === 'USDT') {
          if (amt.includes('.')) {
            tokenAmount = amt.replace('.', '');
            const decimalPlaces = amt.split('.')[1]?.length || 0;
            tokenAmount = tokenAmount + '000000';
            tokenAmount = tokenAmount.substring(0, tokenAmount.length - decimalPlaces + 1);
          } else {
            tokenAmount = amt + '000000';
          }
        }
        // Other ERC20 use 18 decimals
        else {
          if (amt.includes('.')) {
            tokenAmount = amt.replace('.', '');
            const decimalPlaces = amt.split('.')[1]?.length || 0;
            tokenAmount = tokenAmount + '000000000000000000';
            tokenAmount = tokenAmount.substring(0, tokenAmount.length - decimalPlaces + 1);
          } else {
            tokenAmount = amt + '000000000000000000';
          }
        }
        formattedUri = formattedUri + '&uint256=' + tokenAmount;
      }
      // Bitcoin and Bitcoin Cash
      else if (coinKey === 'BTC' || coinKey === 'BCH') {
        formattedUri = formattedUri + '?amount=' + amt;
      }
    }

    return formattedUri;
  };

  const paymentUri = getPaymentUri();

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(coinData.address);
    toast.success('Address copied to clipboard');
  };

  const handleOpenWallet = () => {
    // Open wallet URI (works on mobile)
    if (paymentUri) {
      window.location.href = paymentUri;
    }
  };

  const handleShowFullQR = () => {
    // Copy the payment link
    if (paymentUri) {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Payment link copied to clipboard');
    }
  };

  const handleCoinbasePay = () => {
    // Coinbase Pay integration
    const network = coinInfo.ERC20 ? 'ethereum' : coinInfo.name?.toLowerCase() || 'bitcoin';
    let url = `https://pay.coinbase.com/buy/select-asset?appId=58a3fa2e-617f-4198-81e7-096f5e498c00&addresses={"${address}":["${network}"]}&assets=["${coinInfo.ticker}"]`;

    if (amount && parseFloat(amount) > 0) {
      url += `&presetCryptoAmount=${parseFloat(amount)}`;
    }

    window.location.href = url;
  };

  const handleTransak = () => {
    // Transak fiat-to-crypto integration
    let url = `https://global.transak.com?apiKey=46e5d2a3-6676-483e-82e4-ad542cf81fa2&defaultCryptoCurrency=${coinKey}&walletAddress=${address}`;

    if (amount && parseFloat(amount) > 0) {
      url += `&defaultCryptoAmount=${amount}`;
    }

    window.location.href = url;
  };

  const handleWeb3Send = async () => {
    setWeb3Loading(true);
    setWeb3Error(null);
    setTxHash(null);

    try {
      // Check if MetaMask is installed
      if (!(window as any).ethereum) {
        setWeb3Error('Please install MetaMask to use this feature');
        setWeb3Loading(false);
        return;
      }

      // Request account access
      await (window as any).ethereum.request({ method: 'eth_requestAccounts' });

      // Create provider and signer
      const provider = new ethers.providers.Web3Provider((window as any).ethereum);
      const signer = provider.getSigner();

      if (!amount || parseFloat(amount) <= 0) {
        setWeb3Error('Please enter a valid amount');
        setWeb3Loading(false);
        return;
      }

      let tx;

      // Send ETH
      if (coinKey === 'ETH') {
        tx = await signer.sendTransaction({
          to: address,
          value: ethers.utils.parseEther(amount),
        });
      }
      // Send ERC20 tokens
      else if (coinInfo.ERC20 === true) {
        // ERC20 ABI for transfer function
        const erc20Abi = [
          'function transfer(address to, uint256 amount) returns (bool)',
          'function decimals() view returns (uint8)',
        ];

        // Extract contract address from URI
        // Format: ethereum:0xCONTRACT/transfer?address={address}
        const contractAddress = coinInfo.uri.split(':')[1].split('/')[0];

        const contract = new ethers.Contract(contractAddress, erc20Abi, signer);

        // Get decimals for the token
        let decimals = 18; // Default
        if (coinKey === 'USDC' || coinKey === 'USDT') {
          decimals = 6;
        }

        // Convert amount to token units
        const tokenAmount = ethers.utils.parseUnits(amount, decimals);

        tx = await contract.transfer(address, tokenAmount);
      } else {
        setWeb3Error('This coin is not supported for Web3 direct send');
        setWeb3Loading(false);
        return;
      }

      // Wait for transaction
      const receipt = await tx.wait();
      setTxHash(receipt.transactionHash);

      // Track successful transaction
      ReactGA.event('web3_send_success', {
        addrs: handle,
        coin: coinKey,
        amount: amount,
        txHash: receipt.transactionHash,
      });
    } catch (error: any) {
      console.error('Web3 send error:', error);
      setWeb3Error(error.message || 'Transaction failed. Please try again.');

      // Track failed transaction
      ReactGA.event('web3_send_error', {
        addrs: handle,
        coin: coinKey,
        error: error.message,
      });
    } finally {
      setWeb3Loading(false);
    }
  };

  // Auto-redirect to wallet on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlAmount = urlParams.get('amount');

    if (!loading && handleExists && address && paymentUri) {
      // Auto-redirect immediately
      window.location.replace(paymentUri);
    }
  }, [loading, handleExists, address, paymentUri]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f8fafc',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading payment details...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Handle not found or address not set
  if (!handleExists || !address) {
    return (
      <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
        <Box
          sx={{
            background: gradients.mesh,
            py: 6,
            borderRadius: '0 0 40px 40px',
            mb: 6,
          }}
        >
          <Container maxWidth="md">
            <Box textAlign="center" position="relative" zIndex={1}>
              <Typography variant="h3" fontWeight={800} sx={{ color: '#fff', mb: 2 }}>
                {!handleExists ? 'Handle Not Found' : 'Address Not Set'}
              </Typography>
              <Typography variant="h6" sx={{ color: alpha('#fff', 0.9) }}>
                {!handleExists
                  ? (handle ? `@${handle} doesn't exist` : 'Invalid payment link')
                  : (handle ? `@${handle} hasn't set up a ${coinData.name} address yet` : 'Invalid payment link')}
              </Typography>
            </Box>
          </Container>
        </Box>
        <Container maxWidth="lg" sx={{ mt: 6, pb: 8 }}>
          <GlassCard sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Want to create your own crypto payment profile?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sign up for free and start accepting crypto payments
            </Typography>
            <Box
              component="a"
              href="/"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 3,
                background: gradients.primary,
                color: '#fff',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-block',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0px 8px 24px rgba(99, 102, 241, 0.3)',
                },
              }}
            >
              Sign Up Free
            </Box>
          </GlassCard>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <Box
        sx={{
          background: gradients.mesh,
          py: 6,
          borderRadius: '0 0 40px 40px',
          mb: 6,
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center" position="relative" zIndex={1}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: coinData.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3rem',
                color: '#fff',
                margin: '0 auto 16px',
                boxShadow: '0px 8px 32px rgba(0,0,0,0.15)',
              }}
            >
              {coinData.icon}
            </Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: '#fff', mb: 1 }}>
              Send {coinData.name}{handle ? ` to @${handle}` : ''}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ pb: 8 }}>
        <Grid container spacing={4}>
          {/* Left Column - Payment Methods & Amount */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {/* Amount Input */}
              <GlassCard sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Amount (Optional)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography fontWeight={700} color="text.secondary">
                          {coinData.symbol}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.5rem',
                      fontWeight: 700,
                    },
                  }}
                />
              </GlassCard>

              {/* Web3 Direct Send Error/Success */}
              {web3Error && (
                <Alert severity="error" onClose={() => setWeb3Error(null)} sx={{ mb: 2 }}>
                  {web3Error}
                </Alert>
              )}
              {txHash && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Transaction successful!{' '}
                  <a
                    href={`https://etherscan.io/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    View on Etherscan
                  </a>
                </Alert>
              )}

              {/* Payment Methods */}
              <GlassCard sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Choose Payment Method
                </Typography>

                <Stack spacing={2} sx={{ mt: 2 }}>
                  {/* Web3 Direct Send - Only for ETH and ERC20 */}
                  {(coinKey === 'ETH' || coinInfo.ERC20 === true) && (
                    <GlassCard
                      sx={{
                        p: 3,
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        border: '2px solid transparent',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      <SendIcon
                        sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }}
                      />
                      <Typography variant="subtitle1" fontWeight={700}>
                        Send Directly (MetaMask)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Send instantly using your Web3 wallet
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleWeb3Send}
                        disabled={web3Loading || !amount}
                        sx={{
                          mt: 2,
                          background: gradients.secondary,
                          '&:hover': {
                            background: gradients.secondary,
                            opacity: 0.9,
                          },
                        }}
                        startIcon={web3Loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      >
                        {web3Loading ? 'Sending...' : 'Send Now'}
                      </Button>
                    </GlassCard>
                  )}

                  <GlassCard
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <AccountBalanceWallet
                      sx={{ fontSize: 48, color: 'primary.main', mb: 1 }}
                    />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Open in Wallet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      MetaMask, Trust Wallet, etc.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleOpenWallet}
                      sx={{
                        mt: 2,
                        background: gradients.primary,
                        '&:hover': {
                          background: gradients.primary,
                          opacity: 0.9,
                        },
                      }}
                      endIcon={<OpenInNew />}
                    >
                      Open Wallet
                    </Button>
                  </GlassCard>

                  <GlassCard
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <CreditCard sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Buy with Card (Coinbase)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Buy crypto with credit/debit card
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleCoinbasePay}
                      sx={{
                        mt: 2,
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          background: alpha('#10b981', 0.05),
                        },
                      }}
                      endIcon={<OpenInNew />}
                    >
                      Coinbase Pay
                    </Button>
                  </GlassCard>

                  <GlassCard
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <CreditCard sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Buy with Card (Transak)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fiat to crypto on-ramp
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleTransak}
                      sx={{
                        mt: 2,
                        borderWidth: 2,
                        color: 'warning.main',
                        borderColor: 'warning.main',
                        '&:hover': {
                          borderWidth: 2,
                          borderColor: 'warning.main',
                          background: alpha('#f59e0b', 0.05),
                        },
                      }}
                      endIcon={<OpenInNew />}
                    >
                      Transak
                    </Button>
                  </GlassCard>
                </Stack>
              </GlassCard>
            </Stack>
          </Grid>

          {/* Right Column - QR & Address */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <GlassCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Scan QR Code
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Scan with your crypto wallet app
                </Typography>

                <Box
                  sx={{
                    p: 3,
                    background: '#fff',
                    borderRadius: 3,
                    display: 'inline-block',
                    boxShadow: '0px 4px 24px rgba(0,0,0,0.08)',
                  }}
                >
                  <QRCode value={paymentUri} size={220} />
                </Box>

                <Box sx={{ mt: 4 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="text.secondary"
                    gutterBottom
                  >
                    {coinData.symbol} Address
                  </Typography>

                  {/* Show ENS format only for ETH and ERC20 tokens */}
                  {handle && (coinKey === 'ETH' || coinInfo.ERC20 === true) && (
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        p: 2,
                        borderRadius: 2,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0px 4px 16px rgba(102, 126, 234, 0.3)',
                      }}
                    >
                      <Typography variant="body1" fontWeight={700} sx={{ color: '#fff', fontSize: '1.1rem' }}>
                        {handleToEnsStyle(handle)}
                      </Typography>
                      <Chip
                        label="ENS"
                        size="small"
                        sx={{
                          background: 'rgba(255, 255, 255, 0.25)',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      background: alpha('#000', 0.05),
                      p: 2,
                      borderRadius: 2,
                      fontFamily: 'monospace',
                      fontSize: '1rem',
                      wordBreak: 'break-all',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: alpha('#000', 0.08),
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                      {coinData.address}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyAddress}
                      sx={{ flexShrink: 0 }}
                    >
                      Copy
                    </Button>
                  </Box>
                </Box>
              </GlassCard>

              <GlassCard
                sx={{
                  p: 3,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  border: '2px solid transparent',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <QrCode2 sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  Copy Payment URI
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Copy payment link to clipboard
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={handleShowFullQR}
                  startIcon={<ContentCopy />}
                >
                  Copy URI
                </Button>
              </GlassCard>
            </Stack>
          </Grid>
        </Grid>

        {/* Additional Info */}
        <Box sx={{ mt: 6 }}>
          <GlassCard sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Payment Instructions
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="primary">
                  1. Copy the address or scan the QR code
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use your preferred crypto wallet to send {coinData.symbol}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="primary">
                  2. Enter the amount (if specified)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Make sure to send the exact amount if one is requested
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="primary">
                  3. Confirm and send
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Double-check the address before confirming the transaction
                </Typography>
              </Box>
            </Stack>
          </GlassCard>
        </Box>
      </Container>
    </Box>
  );
};
